// Fake data for the Matrix page. Used while the backend calls (readMatrixView /
// readMatrixData / readMatrixQuestionList) cannot yet deliver results.

const AREAS = [
  { id: 'DE', label: 'DE' },
  { id: 'UK', label: 'UK' },
  { id: 'FR', label: 'FR' },
  { id: 'IT', label: 'IT' },
  { id: 'NL', label: 'NL' },
  { id: 'ES', label: 'ES' }
];

const WAVES = [
  { id: 'W1', label: 'Wave 1', WaveDate: '2026-01-15T00:00:00', SortOrder: 1 },
  { id: 'W2', label: 'Wave 2', WaveDate: '2026-02-15T00:00:00', SortOrder: 2 },
  { id: 'W3', label: 'Wave 3', WaveDate: '2026-03-15T00:00:00', SortOrder: 3 }
];

const GROUP = {
  kpiId: 900, kpiCode: 'Lead', label: 'Lead',
  description: 'Leadprozess gesamt (Kommunikation, Prozess, Erlebnis, Reaktionszeit).',
  baseline: [85, 87, 89]
};

const KPIS = [
  { kpiId: 901, kpiCode: 'Communication Quality', label: 'Communication Quality',
    description: 'Qualität der Kommunikation im Leadprozess.', baseline: [86, 89, 92] },
  { kpiId: 902, kpiCode: 'Framework / Process',   label: 'Framework / Process',
    description: 'Einhaltung des Leadprozess-Frameworks.',    baseline: [90, 88, 87] },
  { kpiId: 903, kpiCode: 'Overall Experience',    label: 'Overall Experience',
    description: 'Gesamterlebnis des Kunden im Leadprozess.', baseline: [84, 87, 90] },
  { kpiId: 904, kpiCode: 'Response Time',         label: 'Response Time',
    description: 'Reaktionszeit auf eingehende Leads.',       baseline: [79, 83, 86] }
];

function hash(str) {
  let s = 0;
  for (const c of String(str)) s = (s * 31 + c.charCodeAt(0)) & 0xffff;
  return s;
}

function areaDelta(code, areaId) {
  return (hash(code + '|' + areaId) % 13) - 6;
}

function clamp(n) { return Math.max(55, Math.min(99, Math.round(n))); }

function buildSurveyWaves(code, baseline, withArea) {
  const targets = withArea ? AREAS : [{ id: '' }];
  const waves = [];
  for (const area of targets) {
    const delta = withArea ? areaDelta(code, area.id) : 0;
    for (let i = 0; i < WAVES.length; i++) {
      const result = clamp(baseline[i] + delta);
      const n = withArea ? 3 + (hash(code + area.id + WAVES[i].id) % 4) : AREAS.length * 3;
      const pointsMax = n;
      const pointsReached = Math.round(pointsMax * result) / 100;
      waves.push({
        Name: WAVES[i].label,
        AggregationLevel2: withArea ? area.id : '',
        Result: result,
        SurveyInstanceCount: n,
        PointsReached: pointsReached,
        PointsMax: pointsMax
      });
    }
  }
  return waves;
}

export function buildFakeMatrixData({ hasAverage } = {}) {
  const withArea = !hasAverage;
  const kpiEntries = KPIS.map(k => ({
    KpiCode: k.kpiCode,
    KpiName: k.label,
    KpiNameShort: k.label,
    KpiGroupCode: GROUP.kpiCode,
    KpiDescription: k.description,
    SurveyWave: buildSurveyWaves(k.kpiCode, k.baseline, withArea)
  }));
  const groupEntry = {
    KpiCode: GROUP.kpiCode,
    KpiName: GROUP.label,
    KpiNameShort: GROUP.label,
    KpiGroupDescription: GROUP.description,
    SurveyWave: buildSurveyWaves(GROUP.kpiCode, GROUP.baseline, withArea)
  };
  const numberOfShops = [];
  const targets = withArea ? AREAS : [{ id: '' }];
  for (const area of targets) {
    for (const w of WAVES) {
      numberOfShops.push({
        SurveyWaveName: w.label,
        AggregationLevel: area.id,
        SurveyInstanceCount: withArea ? 3 + (hash('shop' + area.id + w.id) % 4) : AREAS.length * 3
      });
    }
  }
  return {
    KPI: kpiEntries,
    KPIGroup: [groupEntry],
    NumberOfShopsPerWave: numberOfShops
  };
}

const QUESTIONS_BY_KPI = {
  'Communication Quality': [
    {
      number: 'Q1',
      text: 'Wie bewerten Sie die Kommunikationsqualität beim ersten Kontakt?',
      answers: [
        { text: 'Sehr gut',    share: [55, 60, 64] },
        { text: 'Gut',         share: [30, 28, 26] },
        { text: 'Neutral',     share: [10,  9,  7] },
        { text: 'Schlecht',    share: [ 5,  3,  3] }
      ]
    }
  ],
  'Framework / Process': [
    {
      number: 'Q2',
      text: 'Wurde der Leadprozess gemäß Framework eingehalten?',
      answers: [
        { text: 'Vollständig',        share: [70, 68, 66] },
        { text: 'Überwiegend',        share: [20, 22, 23] },
        { text: 'Teilweise',          share: [ 8,  8,  9] },
        { text: 'Nicht eingehalten',  share: [ 2,  2,  2] }
      ]
    }
  ],
  'Overall Experience': [
    {
      number: 'Q3',
      text: 'Wie zufrieden sind Sie mit dem gesamten Leaderlebnis?',
      answers: [
        { text: 'Begeistert',      share: [45, 50, 54] },
        { text: 'Zufrieden',       share: [38, 35, 33] },
        { text: 'Neutral',         share: [12, 10,  9] },
        { text: 'Unzufrieden',     share: [ 5,  5,  4] }
      ]
    }
  ],
  'Response Time': [
    {
      number: 'Q4',
      text: 'Wie schnell erfolgte die erste persönliche Reaktion?',
      answers: [
        { text: 'Innerhalb 1 Stunde',   share: [42, 48, 52] },
        { text: 'Innerhalb 4 Stunden',  share: [33, 32, 30] },
        { text: 'Innerhalb 24 Stunden', share: [18, 15, 13] },
        { text: 'Länger als 24 Stunden', share: [ 7,  5,  5] }
      ]
    }
  ]
};

function buildQuestionWaves(withArea, nPerArea) {
  const entries = [];
  const targets = withArea ? AREAS : [{ id: '' }];
  for (const area of targets) {
    for (const w of WAVES) {
      entries.push({
        SurveyWaveName: w.label,
        QuestionSurveyInstanceCount: nPerArea,
        AggregationLevel1Value: w.label,
        AggregationLevel2Value: withArea ? area.id : ''
      });
    }
  }
  return entries;
}

function buildAnswerWaves(share, withArea, nPerArea) {
  const entries = [];
  const targets = withArea ? AREAS : [{ id: '' }];
  for (const area of targets) {
    for (let i = 0; i < WAVES.length; i++) {
      const delta = withArea ? (hash(area.id + WAVES[i].id) % 5) - 2 : 0;
      const percent = Math.max(0, share[i] + delta);
      entries.push({
        SurveyWaveName: WAVES[i].label,
        SurveyInstanceCount: Math.round(nPerArea * percent / 100),
        QuestionSurveyInstanceCount: nPerArea,
        CountPercent: percent,
        AggregationLevel1Value: WAVES[i].label,
        AggregationLevel2Value: withArea ? area.id : ''
      });
    }
  }
  return entries;
}

export function buildFakeQuestionList({ hasAverage } = {}) {
  const withArea = !hasAverage;
  const nPerArea = withArea ? 3 : AREAS.length * 3;
  const buildKpi = (kpiCode, label, isGroup = false) => ({
    KpiCode: kpiCode,
    KpiName: label,
    KpiNameShort: label,
    KpiGroupCode: isGroup ? kpiCode : GROUP.kpiCode,
    KpiGroupName: GROUP.label,
    Questions: (QUESTIONS_BY_KPI[kpiCode] || []).map(q => ({
      QuestionNumber: q.number,
      QuestionText: q.text,
      Waves: buildQuestionWaves(withArea, nPerArea),
      Answers: q.answers.map(a => ({
        AnswerText: a.text,
        Waves: buildAnswerWaves(a.share, withArea, nPerArea)
      }))
    }))
  });

  return {
    KPI: KPIS.map(k => buildKpi(k.kpiCode, k.label)),
    KPIGroup: [buildKpi(GROUP.kpiCode, GROUP.label, true)]
  };
}

function uuid(seed) {
  // deterministic pseudo-uuid from seed (presentation only)
  const h = (n) => (hash(seed + n)).toString(16).padStart(4, '0');
  return `${h(1)}${h(2)}-${h(3)}-${h(4)}-${h(5)}-${h(6)}${h(7)}${h(8)}`;
}

function buildView() {
  const kpiChildren = KPIS.map((k, i) => ({
    id: uuid('kpi-' + k.kpiCode),
    label: k.label,
    isOpen: true,
    mode: 'kpi',
    kpiId: k.kpiId,
    kpiCode: k.kpiCode,
    kpiName: k.label,
    isKpiGroup: false,
    position: i + 1,
    showQuestions: true,
    ShowKpiLabelMode: false
  }));

  const rootControl = {
    id: uuid('group-lead'),
    label: GROUP.label,
    isOpen: true,
    mode: 'kpi',
    kpiId: GROUP.kpiId,
    kpiCode: GROUP.kpiCode,
    kpiName: GROUP.label,
    isKpiGroup: true,
    position: 1,
    showQuestions: false,
    children: kpiChildren,
    ShowKpiLabelMode: true
  };

  return {
    id: uuid('view-lead'),
    controls: [rootControl],
    isGeneric: true,
    DefaultLevel: null,
    DefaultFilterData: AREAS.map(a => ({ id: a.id, label: a.label })),
    G6B6FilterData: null,
    Levels: [
      { key: 'Markets', isEnabled: true, id: '3', label: 'Market', group: '1' }
    ],
    label: 'Lead Matrix',
    description: 'Leadprozess-Performance pro Area',
    position: 0,
    type: null,
    isStandard: true,
    isActive: true
  };
}

export function buildFakeMatrixView() {
  const view = buildView();
  const board = JSON.parse(JSON.stringify(view));

  return {
    id: view.id,
    BrandIds: 'LEAD',
    filters: {
      Brand: [
        { id: 'LEAD', label: 'Lead', checked: true }
      ],
      Surveys: [{
        id: 1142,
        label: 'Lead Survey 1142',
        checked: true,
        children: [
          { id: 1142, label: 'Lead 1142', BrandId: 'LEAD', checked: true }
        ]
      }],
      Waves: WAVES.map(w => ({
        id: w.id,
        label: w.label,
        WaveDate: w.WaveDate,
        SortOrder: w.SortOrder,
        checked: true
      })),
      Regions: [],
      Subregions: [],
      NSCs: [],
      SubNSCs: [],
      Areas: AREAS.map(a => ({ id: a.id, label: a.label })),
      Dealergroups: [],
      ClientLocations: [],
      customProperties: []
    },
    boards: [board],
    views: [view]
  };
}
